import {
  faMoneyBillWave,
  faChartLine,
  faBook,
  faShoppingCart,
  faFileInvoice,
  faStore,
  faTruck,
  faExchangeAlt,
  faUsers,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

const reportConfigs = {
  salesSummary: {
    id: "salesSummary",
    titleKey: "reports.reports.salesSummary.title",
    descriptionKey: "reports.reports.salesSummary.description",
    category: "Sales",
    icon: faMoneyBillWave,
    endpoint: "/endpoint/v2/reports/queue/branch/aggregate",
    object: "GetQueueCashDepositStatisticsAnalytics",
    paramsKey: "businessUnitCode",
    chartType: "pie",
    columns: [
      { field: "businessUnitCode", headerKey: "stat.businessUnitCode", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "tcrCode", headerKey: "stat.tcrCode", mobile: { priority: 1, group: "identity" } },
      { field: "successInvoicesNo", headerKey: "stat.successInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "errorInvoicesNo", headerKey: "stat.errorInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "cashInvoicesNo", headerKey: "stat.cashInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "noncashInvoicesNo", headerKey: "stat.nonCashInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "cashTaxInclusiveAmount", headerKey: "stat.cashTaxInclusiveAmount", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "noncashTaxInclusiveAmount", headerKey: "stat.nonCashTaxInclusiveAmount", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "correctiveTotalWithVat", headerKey: "stat.correctiveTotalWithVat", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "cashCorrectiveNo", headerKey: "stat.cashCorrectiveNo", mobile: { priority: 2, group: "status" } },
      { field: "noncashCorrectiveNo", headerKey: "stat.nonCashCorrectiveNo", mobile: { priority: 2, group: "status" } },
      { field: "banknoteCashTot", headerKey: "reports.columns.banknoteCashTot", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "cashInit", headerKey: "stat.cashInit", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "cashDeposit", headerKey: "stat.cashDeposit", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "cashWithdraw", headerKey: "stat.cashWithdraw", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "cashEnd", headerKey: "stat.cashEnd", isMoney: true, mobile: { priority: 2, group: "financial" } },
    ],
    summaryFields: [
      { key: "cashTaxInclusiveAmount", labelKey: "stat.cashTaxInclusiveAmount", isMoney: true, variant: "primary" },
      { key: "noncashTaxInclusiveAmount", labelKey: "stat.nonCashTaxInclusiveAmount", isMoney: true, variant: "blue" },
      { key: "successInvoicesNo", labelKey: "stat.successInvoicesNo", variant: "purple" },
      { key: "correctiveTotalWithVat", labelKey: "stat.correctiveTotalWithVat", isMoney: true, variant: "warning" },
      { key: "errorInvoicesNo", labelKey: "stat.errorInvoicesNo", variant: "danger" },
    ],
    chartFields: {
      labels: "businessUnitCode",
      datasets: [
        { key: "cashTaxInclusiveAmount", labelKey: "stat.cashTaxInclusiveAmount", color: "#007567" },
        { key: "noncashTaxInclusiveAmount", labelKey: "stat.nonCashTaxInclusiveAmount", color: "#3b82f6" },
      ],
    },
  },

  dailySalesAnalytics: {
    id: "dailySalesAnalytics",
    titleKey: "reports.reports.dailySalesAnalytics.title",
    descriptionKey: "reports.reports.dailySalesAnalytics.description",
    category: "Sales",
    icon: faChartLine,
    endpoint: "/endpoint/v2/reports/queue/branch/analytic",
    object: "GetQueueCashDepositStatisticsAnalytics",
    paramsKey: "buCodes",
    chartType: "pie",
    columns: [
      { field: "recordDate", headerKey: "table.recordDate", mobile: { priority: 1, isTitle: true, group: "dates" } },
      { field: "businessUnitCode", headerKey: "stat.businessUnitCode", mobile: { priority: 1, group: "identity" } },
      { field: "tcrCode", headerKey: "stat.tcrCode", mobile: { priority: 1, group: "identity" } },
      { field: "cashTaxInclusiveAmount", headerKey: "stat.cashTaxInclusiveAmount", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "noncashTaxInclusiveAmount", headerKey: "stat.nonCashTaxInclusiveAmount", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "successInvoicesNo", headerKey: "stat.successInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "errorInvoicesNo", headerKey: "stat.errorInvoicesNo", mobile: { priority: 1, group: "status" } },
    ],
    summaryFields: [
      { key: "cashTaxInclusiveAmount", labelKey: "stat.cashTaxInclusiveAmount", isMoney: true, variant: "primary" },
      { key: "noncashTaxInclusiveAmount", labelKey: "stat.nonCashTaxInclusiveAmount", isMoney: true, variant: "blue" },
      { key: "successInvoicesNo", labelKey: "stat.successInvoicesNo", variant: "purple" },
    ],
    chartFields: {
      labels: "recordDate",
      datasets: [
        { key: "cashTaxInclusiveAmount", labelKey: "stat.cashTaxInclusiveAmount", color: "#007567" },
        { key: "noncashTaxInclusiveAmount", labelKey: "stat.nonCashTaxInclusiveAmount", color: "#3b82f6" },
      ],
    },
    transform: (data) => {
      const grouped = {};
      data.forEach((item) => {
        const dateKey = item.recordDate
          ? moment(item.recordDate).format("YYYY-MM-DD")
          : "N/A";
        const buCode = item.businessUnitCode || "N/A";
        const key = `${dateKey}_${buCode}`;
        if (!grouped[key]) {
          grouped[key] = {
            recordDate: item.recordDate ? moment(item.recordDate).format("DD/MM/YYYY") : "N/A",
            businessUnitCode: buCode,
            tcrCode: item.tcrCode || "",
            cashTaxInclusiveAmount: 0,
            noncashTaxInclusiveAmount: 0,
            successInvoicesNo: 0,
            errorInvoicesNo: 0,
            _sortDate: dateKey,
          };
        }
        grouped[key].cashTaxInclusiveAmount += item.cashTaxInclusiveAmount || 0;
        grouped[key].noncashTaxInclusiveAmount += item.noncashTaxInclusiveAmount || 0;
        grouped[key].successInvoicesNo += item.successInvoicesNo || 0;
        grouped[key].errorInvoicesNo += item.errorInvoicesNo || 0;
      });
      return Object.values(grouped).sort((a, b) =>
        a._sortDate.localeCompare(b._sortDate)
      );
    },
  },

  salesBookStats: {
    id: "salesBookStats",
    titleKey: "reports.reports.salesBookStats.title",
    descriptionKey: "reports.reports.salesBookStats.description",
    category: "Sales",
    icon: faBook,
    endpoint: "endpoint/v2/sale/statistics",
    object: "GetSaleBookStatistics",
    usesPeriod: true,
    paramsKey: "businessUnitCode",
    chartType: "pie",
    columns: [
      { field: "category", headerKey: "reports.columns.category", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "invoiceCount", headerKey: "reports.columns.invoiceCount", mobile: { priority: 1, group: "status" } },
      { field: "salesWithoutVat", headerKey: "books.salesWoVat", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "vatAmount", headerKey: "books.vatAmount", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "salesWithVat", headerKey: "reports.columns.salesWithVat", isMoney: true, mobile: { priority: 1, group: "financial" } },
    ],
    summaryFields: [
      { key: "salesWithVat", labelKey: "reports.columns.salesWithVat", isMoney: true, variant: "primary", aggregate: "sum" },
      { key: "salesWithoutVat", labelKey: "books.salesWoVat", isMoney: true, variant: "blue", aggregate: "sum" },
      { key: "vatAmount", labelKey: "books.vatAmount", isMoney: true, variant: "purple", aggregate: "sum" },
      { key: "invoiceCount", labelKey: "reports.columns.invoiceCount", variant: "warning", aggregate: "sum" },
    ],
    chartFields: {
      labels: "category",
      datasets: [
        { key: "salesWithVat", labelKey: "reports.columns.salesWithVat", color: "#007567" },
      ],
    },
    transformKey: "salesBook",
  },

  purchaseSummary: {
    id: "purchaseSummary",
    titleKey: "reports.reports.purchaseSummary.title",
    descriptionKey: "reports.reports.purchaseSummary.description",
    category: "Purchases",
    icon: faShoppingCart,
    endpoint: "/endpoint/v2/reports/mypurchase/branch/aggregate",
    companyEndpoint: "/endpoint/v2/reports/mypurchase/company/aggregate",
    object: "GetMyPurchaseCashDepositStatisticsAnalytics",
    paramsKey: "businessUnitCode",
    hasPurchaseUnit: true,
    chartType: "pie",
    columns: [
      { field: "purchaseUnit", headerKey: "purchases.purchaseUnit", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "allCurrencyInvoices", headerKey: "reports.columns.invoiceCount", mobile: { priority: 1, group: "status" } },
      { field: "cisAcceptedStatusInvoices", headerKey: "reports.columns.cisAcceptedStatusInvoices", mobile: { priority: 1, group: "status" } },
      { field: "cisDeliveredStatusInvoices", headerKey: "reports.columns.cisDeliveredStatusInvoices", mobile: { priority: 1, group: "status" } },
      { field: "cisPartiallyPaidStatusInvoices", headerKey: "reports.columns.cisPartiallyPaidStatusInvoices", mobile: { priority: 1, group: "status" } },
      { field: "cisPaidStatusInvoices", headerKey: "reports.columns.cisPaidStatusInvoices", mobile: { priority: 1, group: "status" } },
      { field: "cisRejectedStatusInvoices", headerKey: "reports.columns.cisRejectedStatusInvoices", mobile: { priority: 1, group: "status" } },
      { field: "valueWoVatAcceptedAll", headerKey: "reports.columns.valueWoVatAcceptedAll", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "valueVatAcceptedAll", headerKey: "reports.columns.valueVatAcceptedAll", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "valueWithVatAcceptedAll", headerKey: "reports.columns.valueWithVatAcceptedAll", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "valueWoVatRejectedAll", headerKey: "reports.columns.valueWoVatRejectedAll", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "valueVatRejectedAll", headerKey: "reports.columns.valueVatRejectedAll", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "valueWithVatRejectedAll", headerKey: "reports.columns.valueWithVatRejectedAll", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "onlyFisc", headerKey: "reports.columns.onlyFisc", mobile: { priority: 1, group: "status" } },
      { field: "importCustomNo", headerKey: "reports.columns.importCustomNo", mobile: { priority: 1, group: "status" } },
    ],
    summaryFields: [
      { key: "valueWithVatAcceptedAll", labelKey: "reports.columns.valueWithVatAcceptedAll", isMoney: true, variant: "primary" },
      { key: "valueWithVatRejectedAll", labelKey: "reports.columns.valueWithVatRejectedAll", isMoney: true, variant: "danger" },
      { key: "valueVatAcceptedAll", labelKey: "reports.columns.valueVatAcceptedAll", isMoney: true, variant: "blue" },
      { key: "allCurrencyInvoices", labelKey: "reports.columns.invoiceCount", variant: "purple" },
      { key: "cisAcceptedStatusInvoices", labelKey: "reports.columns.cisAcceptedStatusInvoices", variant: "success" },
    ],
    chartFields: {
      labels: "purchaseUnit",
      datasets: [
        { key: "valueWithVatAcceptedAll", labelKey: "reports.columns.valueWithVatAcceptedAll", color: "#007567" },
      ],
    },
  },

  purchaseBookStats: {
    id: "purchaseBookStats",
    titleKey: "reports.reports.purchaseBookStats.title",
    descriptionKey: "reports.reports.purchaseBookStats.description",
    category: "Purchases",
    icon: faBook,
    endpoint: "endpoint/v2/purchase/statistics",
    object: "GetPurchaseBookStatistics",
    usesPeriod: true,
    hasPurchaseUnit: true,
    paramsKey: "businessUnitCode",
    chartType: "pie",
    columns: [
      { field: "category", headerKey: "reports.columns.category", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "invoiceCount", headerKey: "reports.columns.invoiceCount", mobile: { priority: 1, group: "status" } },
      { field: "purchaseWithoutVat", headerKey: "reports.columns.purchaseWithoutVat", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "vatAmount", headerKey: "books.vatAmount", isMoney: true, mobile: { priority: 1, group: "financial" } },
    ],
    summaryFields: [
      { key: "purchaseWithoutVat", labelKey: "reports.columns.purchaseWithoutVat", isMoney: true, variant: "primary", aggregate: "sum" },
      { key: "vatAmount", labelKey: "books.vatAmount", isMoney: true, variant: "blue", aggregate: "sum" },
      { key: "invoiceCount", labelKey: "reports.columns.invoiceCount", variant: "warning", aggregate: "sum" },
    ],
    chartFields: {
      labels: "category",
      datasets: [
        { key: "purchaseWithoutVat", labelKey: "reports.columns.purchaseWithoutVat", color: "#007567" },
      ],
    },
    transformKey: "purchaseBook",
  },

  customInvoiceStats: {
    id: "customInvoiceStats",
    titleKey: "reports.reports.customInvoiceStats.title",
    descriptionKey: "reports.reports.customInvoiceStats.description",
    category: "Documents",
    icon: faFileInvoice,
    endpoint: "/endpoint/v2/reports/myinvoice/branch/aggregate",
    companyEndpoint: "/endpoint/v2/reports/myinvoice/company/aggregate",
    object: "GetCustomInvoiceCashDepositStatisticsAnalytics",
    paramsKey: "businessUnitCode",
    chartType: "pie",
    columns: [
      { field: "businessUnitCode", headerKey: "stat.businessUnitCode", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "tcrCode", headerKey: "stat.tcrCode", mobile: { priority: 1, group: "identity" } },
      { field: "salesWithVat", headerKey: "reports.columns.salesWithVat", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "salesNoVat", headerKey: "reports.columns.salesNoVat", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "salesVat", headerKey: "reports.columns.salesVat", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "totalCash", headerKey: "reports.columns.totalCash", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "totalNoncash", headerKey: "reports.columns.totalNoncash", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "totalEinvoiceSales", headerKey: "reports.columns.totalEinvoiceSales", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "correctiveWithVat", headerKey: "reports.columns.correctiveWithVat", isMoney: true, mobile: { priority: 2, group: "financial" } },
      { field: "onlyFiscalizationInvoicesNo", headerKey: "reports.columns.onlyFiscalizationInvoicesNo", mobile: { priority: 2, group: "status" } },
      { field: "cashInvoicesNo", headerKey: "stat.cashInvoicesNo", mobile: { priority: 2, group: "status" } },
      { field: "noncashInvoicesNo", headerKey: "stat.nonCashInvoicesNo", mobile: { priority: 2, group: "status" } },
      { field: "einvoiceInvoicesNo", headerKey: "reports.columns.einvoiceInvoicesNo" },
      { field: "draftInvoicesNo", headerKey: "reports.columns.draftInvoicesNo" },
      { field: "approvedInvoicesNo", headerKey: "reports.columns.approvedInvoicesNo" },
      { field: "readyForApprovalInvoicesNo", headerKey: "reports.columns.readyForApprovalInvoicesNo" },
      { field: "errorInvoicesNo", headerKey: "stat.errorInvoicesNo" },
      { field: "retryInvoicesNo", headerKey: "reports.columns.retryInvoicesNo" },
      { field: "correctiveInvoicesNo", headerKey: "reports.columns.correctiveInvoicesNo" },
      { field: "deletedInvoicesNo", headerKey: "reports.columns.deletedInvoicesNo" },
      { field: "selfInvoicesNo", headerKey: "stat.selfInvoicesNo" },
      { field: "foreignInvoicesNo", headerKey: "stat.foreignInvoicesNo" },
      { field: "allCurrencyInvoicesNo", headerKey: "stat.allCurrencyInvoicesNo" },
      { field: "eurCurrencyInvoicesNo", headerKey: "stat.eurCurrencyInvoicesNo" },
      { field: "usdCurrencyInvoicesNo", headerKey: "stat.usdCurrencyInvoicesNo" },
      { field: "subseqInvoicesNo", headerKey: "stat.subseqInvoicesNo" },
    ],
    summaryFields: [
      { key: "salesWithVat", labelKey: "reports.columns.salesWithVat", isMoney: true, variant: "primary" },
      { key: "totalCash", labelKey: "reports.columns.totalCash", isMoney: true, variant: "blue" },
      { key: "totalNoncash", labelKey: "reports.columns.totalNoncash", isMoney: true, variant: "purple" },
      { key: "approvedInvoicesNo", labelKey: "reports.columns.approvedInvoicesNo", variant: "success" },
      { key: "errorInvoicesNo", labelKey: "stat.errorInvoicesNo", variant: "danger" },
    ],
    chartFields: {
      labels: "businessUnitCode",
      datasets: [
        { key: "totalCash", labelKey: "reports.columns.totalCash", color: "#007567" },
        { key: "totalNoncash", labelKey: "reports.columns.totalNoncash", color: "#3b82f6" },
      ],
    },
  },

  posStats: {
    id: "posStats",
    titleKey: "reports.reports.posStats.title",
    descriptionKey: "reports.reports.posStats.description",
    category: "Documents",
    icon: faStore,
    endpoint: "/endpoint/v2/reports/mypos/branch/aggregate",
    companyEndpoint: "/endpoint/v2/reports/mypos/company/aggregate",
    object: "GetCustomInvoiceCashDepositStatisticsAnalytics",
    paramsKey: "businessUnitCode",
    chartType: "pie",
    columns: [
      { field: "businessUnitCode", headerKey: "stat.businessUnitCode", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "tcrCode", headerKey: "stat.tcrCode", mobile: { priority: 1, group: "identity" } },
      { field: "cashInvoicesNo", headerKey: "stat.cashInvoicesNo", mobile: { priority: 1, group: "financial" } },
      { field: "noncashInvoicesNo", headerKey: "stat.nonCashInvoicesNo", mobile: { priority: 1, group: "financial" } },
      { field: "onlyFiscalizationInvoicesNo", headerKey: "reports.columns.onlyFiscalizationInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "draftInvoicesNo", headerKey: "reports.columns.draftInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "approvedInvoicesNo", headerKey: "reports.columns.approvedInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "errorInvoicesNo", headerKey: "stat.errorInvoicesNo", mobile: { priority: 1, group: "status" } },
      { field: "retryInvoicesNo", headerKey: "reports.columns.retryInvoicesNo", mobile: { priority: 2, group: "status" } },
      { field: "deletedInvoicesNo", headerKey: "reports.columns.deletedInvoicesNo", mobile: { priority: 2, group: "status" } },
      { field: "allCurrencyInvoicesNo", headerKey: "stat.allCurrencyInvoicesNo", mobile: { priority: 2, group: "status" } },
      { field: "eurCurrencyInvoicesNo", headerKey: "stat.eurCurrencyInvoicesNo", mobile: { priority: 2, group: "status" } },
      { field: "usdCurrencyInvoicesNo", headerKey: "stat.usdCurrencyInvoicesNo", mobile: { priority: 2, group: "status" } },
    ],
    summaryFields: [
      { key: "cashInvoicesNo", labelKey: "stat.cashInvoicesNo", variant: "primary" },
      { key: "noncashInvoicesNo", labelKey: "stat.nonCashInvoicesNo", variant: "blue" },
      { key: "onlyFiscalizationInvoicesNo", labelKey: "reports.columns.onlyFiscalizationInvoicesNo", variant: "purple" },
      { key: "approvedInvoicesNo", labelKey: "reports.columns.approvedInvoicesNo", variant: "success" },
      { key: "errorInvoicesNo", labelKey: "stat.errorInvoicesNo", variant: "danger" },
    ],
    chartFields: {
      labels: "businessUnitCode",
      datasets: [
        { key: "cashInvoicesNo", labelKey: "stat.cashInvoicesNo", color: "#007567" },
        { key: "noncashInvoicesNo", labelKey: "stat.nonCashInvoicesNo", color: "#3b82f6" },
      ],
    },
  },

  waybillStats: {
    id: "waybillStats",
    titleKey: "reports.reports.waybillStats.title",
    descriptionKey: "reports.reports.waybillStats.description",
    category: "Documents",
    icon: faTruck,
    endpoint: "/endpoint/v2/reports/mywtn/branch/aggregate",
    companyEndpoint: "/endpoint/v2/reports/mywtn/company/aggregate",
    object: "GetMyWtnCashDepositStatisticsAnalytics",
    paramsKey: "businessUnitCode",
    chartType: "pie",
    columns: [
      { field: "businessUnitCode", headerKey: "stat.businessUnitCode", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "tcrCode", headerKey: "stat.tcrCode", mobile: { priority: 1, group: "identity" } },
      { field: "approvedWtnNo", headerKey: "reports.columns.approvedWtnNo", mobile: { priority: 1, group: "status" } },
      { field: "errorWtnNo", headerKey: "reports.columns.errorWtnNo", mobile: { priority: 1, group: "status" } },
      { field: "retryWtnNo", headerKey: "reports.columns.retryWtnNo", mobile: { priority: 1, group: "status" } },
      { field: "deletedWtnNo", headerKey: "reports.columns.deletedWtnNo", mobile: { priority: 1, group: "status" } },
      { field: "draftWtnNo", headerKey: "reports.columns.draftWtnNo", mobile: { priority: 1, group: "status" } },
      { field: "transferTransactionNo", headerKey: "reports.columns.transferTransactionNo", mobile: { priority: 1, group: "status" } },
      { field: "doorTransactionNo", headerKey: "reports.columns.doorTransactionNo", mobile: { priority: 2, group: "status" } },
      { field: "salesTransactionNo", headerKey: "reports.columns.salesTransactionNo", mobile: { priority: 2, group: "status" } },
      { field: "examinationTransactionNo", headerKey: "reports.columns.examinationTransactionNo", mobile: { priority: 2, group: "status" } },
      { field: "wtnTypeNo", headerKey: "reports.columns.wtnTypeNo", mobile: { priority: 2, group: "status" } },
      { field: "saleTypeNo", headerKey: "reports.columns.saleTypeNo", mobile: { priority: 2, group: "status" } },
      { field: "companyTransporterNo", headerKey: "customers.company", mobile: { priority: 2, group: "identity" } },
      { field: "idTransporterNo", headerKey: "status.individ", mobile: { priority: 2, group: "identity" } },
    ],
    summaryFields: [
      { key: "approvedWtnNo", labelKey: "reports.columns.approvedWtnNo", variant: "primary" },
      { key: "draftWtnNo", labelKey: "reports.columns.draftWtnNo", variant: "blue" },
      { key: "errorWtnNo", labelKey: "reports.columns.errorWtnNo", variant: "danger" },
      { key: "transferTransactionNo", labelKey: "reports.columns.transferTransactionNo", variant: "purple" },
      { key: "salesTransactionNo", labelKey: "reports.columns.salesTransactionNo", variant: "success" },
    ],
    chartFields: {
      labels: "businessUnitCode",
      datasets: [
        { key: "approvedWtnNo", labelKey: "reports.columns.approvedWtnNo", color: "#007567" },
        { key: "errorWtnNo", labelKey: "reports.columns.errorWtnNo", color: "#ef4444" },
        { key: "draftWtnNo", labelKey: "reports.columns.draftWtnNo", color: "#3b82f6" },
      ],
    },
  },

  transportInvoiceStats: {
    id: "transportInvoiceStats",
    titleKey: "reports.reports.transportInvoiceStats.title",
    descriptionKey: "reports.reports.transportInvoiceStats.description",
    category: "Documents",
    icon: faExchangeAlt,
    endpoint: "/endpoint/v2/reports/myti/company/aggregate",
    object: "GetMyTiCashDepositStatisticsAnalytics",
    paramsKey: "businessUnitCode",
    chartType: "pie",
    columns: [
      { field: "businessUnitCode", headerKey: "stat.businessUnitCode", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "tcrCode", headerKey: "stat.tcrCode", mobile: { priority: 1, group: "identity" } },
      { field: "approvedTiNo", headerKey: "reports.columns.approvedTiNo", mobile: { priority: 1, group: "status" } },
      { field: "errorTiNo", headerKey: "reports.columns.errorTiNo", mobile: { priority: 1, group: "status" } },
      { field: "retryTiNo", headerKey: "reports.columns.retryTiNo", mobile: { priority: 1, group: "status" } },
      { field: "deletedTiNo", headerKey: "reports.columns.deletedTiNo", mobile: { priority: 1, group: "status" } },
      { field: "draftTiNo", headerKey: "reports.columns.draftTiNo", mobile: { priority: 1, group: "status" } },
      { field: "processingTransactionNo", headerKey: "reports.columns.processingTransactionNo", mobile: { priority: 1, group: "status" } },
      { field: "repairTransactionNo", headerKey: "reports.columns.repairTransactionNo", mobile: { priority: 2, group: "status" } },
      { field: "examinationTransactionNo", headerKey: "reports.columns.examinationTransactionNo", mobile: { priority: 2, group: "status" } },
      { field: "companyTransporterNo", headerKey: "customers.company", mobile: { priority: 2, group: "identity" } },
      { field: "idTransporterNo", headerKey: "status.individ", mobile: { priority: 2, group: "identity" } },
    ],
    summaryFields: [
      { key: "approvedTiNo", labelKey: "reports.columns.approvedTiNo", variant: "primary" },
      { key: "draftTiNo", labelKey: "reports.columns.draftTiNo", variant: "blue" },
      { key: "errorTiNo", labelKey: "reports.columns.errorTiNo", variant: "danger" },
      { key: "processingTransactionNo", labelKey: "reports.columns.processingTransactionNo", variant: "purple" },
      { key: "repairTransactionNo", labelKey: "reports.columns.repairTransactionNo", variant: "warning" },
    ],
    chartFields: {
      labels: "businessUnitCode",
      datasets: [
        { key: "approvedTiNo", labelKey: "reports.columns.approvedTiNo", color: "#007567" },
        { key: "errorTiNo", labelKey: "reports.columns.errorTiNo", color: "#ef4444" },
        { key: "draftTiNo", labelKey: "reports.columns.draftTiNo", color: "#3b82f6" },
      ],
    },
  },

  topCustomers: {
    id: "topCustomers",
    titleKey: "reports.reports.topCustomers.title",
    descriptionKey: "reports.reports.topCustomers.description",
    category: "Partners",
    icon: faUsers,
    endpoint: "/endpoint/v2/reports/customers",
    noObject: true,
    paramsKey: null,
    chartType: "pie",
    hasLimit: true,
    columns: [
      { field: "rank", headerKey: "reports.columns.rank", mobile: { priority: 1, group: "identity" } },
      { field: "buyerName", headerKey: "topten.customerName", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "buyerNuis", headerKey: "topten.buyerNuis", mobile: { priority: 1, group: "identity" } },
      { field: "purchaseTot", headerKey: "topten.purchaseTot", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "purchaseNo", headerKey: "topten.purchaseNo", mobile: { priority: 1, group: "status" } },
    ],
    summaryFields: [
      { key: "purchaseTot", labelKey: "reports.summaryLabels.totalRevenue", isMoney: true, variant: "primary", aggregate: "sum" },
      { key: "purchaseNo", labelKey: "reports.summaryLabels.totalPurchases", variant: "blue", aggregate: "sum" },
      { key: "_count", labelKey: "reports.summaryLabels.uniqueCustomers", variant: "purple" },
    ],
    chartFields: {
      labels: "buyerName",
      datasets: [
        { key: "purchaseTot", labelKey: "reports.summaryLabels.totalPurchases", color: "#007567" },
      ],
    },
    transform: (data) => data.map((item, idx) => ({ ...item, rank: idx + 1 })),
  },

  topSuppliers: {
    id: "topSuppliers",
    titleKey: "reports.reports.topSuppliers.title",
    descriptionKey: "reports.reports.topSuppliers.description",
    category: "Partners",
    icon: faHandshake,
    endpoint: "/endpoint/v2/reports/suppliers",
    noObject: true,
    paramsKey: null,
    chartType: "pie",
    hasLimit: true,
    columns: [
      { field: "rank", headerKey: "reports.columns.rank", mobile: { priority: 1, group: "identity" } },
      { field: "sellerName", headerKey: "topten.supplierName", mobile: { priority: 1, isTitle: true, group: "identity" } },
      { field: "sellerNuis", headerKey: "topten.sellerNuis", mobile: { priority: 1, group: "identity" } },
      { field: "salesTot", headerKey: "topten.salesTot", isMoney: true, mobile: { priority: 1, group: "financial" } },
      { field: "salesNo", headerKey: "topten.salesNo", mobile: { priority: 1, group: "status" } },
    ],
    summaryFields: [
      { key: "salesTot", labelKey: "reports.summaryLabels.totalSpent", isMoney: true, variant: "primary", aggregate: "sum" },
      { key: "salesNo", labelKey: "reports.summaryLabels.totalSales", variant: "blue", aggregate: "sum" },
      { key: "_count", labelKey: "reports.summaryLabels.uniqueSuppliers", variant: "purple" },
    ],
    chartFields: {
      labels: "sellerName",
      datasets: [
        { key: "salesTot", labelKey: "reports.summaryLabels.totalSales", color: "#007567" },
      ],
    },
    transform: (data) => data.map((item, idx) => ({ ...item, rank: idx + 1 })),
  },
};

export const reportCategories = [
  { id: "Sales", labelKey: "reports.categories.sales" },
  { id: "Purchases", labelKey: "reports.categories.purchases" },
  { id: "Documents", labelKey: "reports.categories.documents" },
  { id: "Partners", labelKey: "reports.categories.partners" },
];

// Transform functions that need translation - called with t() from the component
export const getTransformedData = (config, data, t) => {
  if (config.transformKey === "salesBook") {
    const row = data[0];
    if (!row) return [];
    const categories = [
      { category: t("books.isType1Exempted"), invoiceCount: row.type1InvoicesNo, salesWithoutVat: row.type1SalesWithoutVat, vatAmount: row.type1VatAmount, salesWithVat: row.type1SalesWithVat },
      { category: t("books.isType2Exempted"), invoiceCount: row.type2InvoicesNo, salesWithoutVat: row.type2SalesWithoutVat, vatAmount: row.type2VatAmount, salesWithVat: row.type2SalesWithVat },
      { category: t("books.isExportOfGoodsExempted"), invoiceCount: row.exportOfGoodsInvoicesNo, salesWithoutVat: row.exportOfGoodsSalesWithoutVat, vatAmount: row.exportOfGoodsVatAmount, salesWithVat: row.exportOfGoodsSalesWithVat },
      { category: "Tjeter, jo nenet 51,53 dhe 54", invoiceCount: row.taxFreeInvoicesNo, salesWithoutVat: row.taxFreeSalesWithoutVat, vatAmount: row.taxFreeVatAmount, salesWithVat: row.taxFreeSalesWithVat },
      { category: "Marzhi I fitimit", invoiceCount: row.marginSchemeInvoicesNo, salesWithoutVat: row.marginSchemeSalesWithoutVat, vatAmount: row.marginSchemeVatAmount, salesWithVat: row.marginSchemeSalesWithVat },
      { category: t("books.isReverseOnSale"), invoiceCount: row.reverseChargeInvoicesNo, salesWithoutVat: row.reverseChargeSalesWithoutVat, vatAmount: row.reverseChargeSalesVatAmount, salesWithVat: row.reverseChargeSalesWithVat },
      { category: t("books.isZeroVatRated"), invoiceCount: row.zeroPercentInSupplyNo, salesWithoutVat: row.zeroRateSalesWithoutVat, vatAmount: row.zeroRateVatAmount, salesWithVat: row.zeroRateSalesWithVat },
      { category: t("books.isStandardSale"), invoiceCount: row.standardSalesInvoicesNo, salesWithoutVat: row.standardSalesWithoutVat, vatAmount: row.standardSalesVatAmount, salesWithVat: row.standardSalesWithVat },
      { category: t("books.badDebt"), invoiceCount: row.badDebtInvoicesNo, salesWithoutVat: row.badDebtSalesWithoutVat, vatAmount: row.badDebtSalesVatAmount, salesWithVat: row.badDebtSalesWithVat },
    ];
    return categories;
  }

  if (config.transformKey === "purchaseBook") {
    const row = data[0];
    if (!row) return [];
    const categories = [
      { category: t("books.zero%exempted"), invoiceCount: row.exemptedInvoicesNo, purchaseWithoutVat: row.exemptedInvoicesWithoutVat, vatAmount: row.exemptedInvoicesVatAmount },
      { category: t("books.isReverseChargePurchases"), invoiceCount: row.reverseChargeInvoicesNo, purchaseWithoutVat: row.reverseChargePurchaseWithoutVat, vatAmount: row.reverseChargePurchaseVatAmount },
      { category: t("books.isStandartPurchase"), invoiceCount: row.standardPurchaseInvoicesNo, purchaseWithoutVat: row.standardPurchaseWithoutVat, vatAmount: row.standardPurchaseVatAmount },
      { category: t("books.isStandardImport"), invoiceCount: row.standardImportNo, purchaseWithoutVat: row.standardImportWithoutVat, vatAmount: row.standardImportVatAmount },
      { category: t("books.badDebt"), invoiceCount: row.badDebtInvoicesNo, purchaseWithoutVat: row.badDebtPurchaseWithoutVat, vatAmount: row.badDebtPurchaseVatAmount },
      { category: t("books.importExempted"), invoiceCount: row.exemptedImportNo, purchaseWithoutVat: row.exemptedImportWithoutVat, vatAmount: row.exemptedImportVatAmount },
    ];
    return categories;
  }

  return null;
};

export default reportConfigs;
